/** Formatações de exibição. Nenhum componente monta string de preço na mão. */

const integerFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

/**
 * Quebra o preço em partes para o layout com centavos sobrescritos
 * (`R$ 70⁹⁰`). Centavos zerados são omitidos, como no layout de referência.
 */
export function priceParts(value: number): { integer: string; cents: string | null } {
  const rounded = Math.round(value * 100)
  const integer = Math.trunc(rounded / 100)
  const cents = Math.abs(rounded % 100)

  return {
    integer: integerFormatter.format(integer),
    cents: cents === 0 ? null : String(cents).padStart(2, '0'),
  }
}

/** `R$ 1.376,90` — usado onde não há espaço para o sobrescrito. */
export function formatCurrency(value: number): string {
  return currencyFormatter.format(value)
}

/** `9% OFF`. Retorna null quando não há desconto para exibir. */
export function formatDiscount(percent?: number | null): string | null {
  if (!percent || percent <= 0) return null
  return `${Math.round(percent)}% OFF`
}

/** `+1 mil vendidos`, `+50 vendidos`. Null quando o dado não veio. */
export function formatSoldCount(count?: number | null): string | null {
  if (!count || count <= 0) return null
  if (count >= 1000) {
    const thousands = Math.floor(count / 1000)
    return `+${thousands} mil vendidos`
  }
  return `+${Math.floor(count / 10) * 10} vendidos`
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
})

export function formatDate(iso: string): string {
  return dateFormatter.format(new Date(iso))
}

/** Máscara progressiva de CPF: `000.000.000-00`. */
export function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

/** Máscara progressiva de telefone: `(00) 00000-0000`. */
export function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})$/, '$1-$2')
  }
  return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2')
}

/** Esconde o miolo do e-mail em telas de segurança: `da•••@lionot.com`. */
export function maskEmail(email: string): string {
  const [user = '', domain = ''] = email.split('@')
  if (!domain) return email
  const visible = user.slice(0, 2)
  return `${visible}${'•'.repeat(Math.max(user.length - 2, 3))}@${domain}`
}
